import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.5, 1, 1, 0.5]);

  return (
    <div ref={ref} className="overflow-hidden rounded-lg my-20 sm:my-28">
      <motion.img src={src} alt={alt} className="w-full max-w-3xl mx-auto" style={{ y, opacity }} />
    </div>
  );
};

const TextBlock = ({ children, id }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-100px' }}
    transition={{ duration: 0.8, delay: 0.2 }}
    className="text-center space-y-4 sm:space-y-5"
  >
    {children}
  </motion.div>
);

const Title = ({ children }) => (
  <h2 className="text-xl sm:text-2xl md:text-3xl tracking-tight" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
    {children}
  </h2>
);

const Body = ({ children }) => (
  <p className="text-base sm:text-lg md:text-xl leading-relaxed opacity-80" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
    {children}
  </p>
);

const PullQuote = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 1.2 }}
    className="my-20 sm:my-28 py-10 sm:py-14 text-center"
  >
    <p className="text-2xl sm:text-3xl md:text-4xl italic leading-snug max-w-2xl mx-auto" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
      {children}
    </p>
  </motion.div>
);

const Perforation = () => (
  <div className="my-16 sm:my-24 flex items-center justify-center">
    <div className="perforation-divider w-full max-w-md" />
  </div>
);

const ScrollContent = () => {
  const setImages = [
    'https://customer-assets.emergentagent.com/job_tss-admission-portal/artifacts/tyr3w8xe_Screenshot%202026-04-14%20at%206.27.34%E2%80%AFAM.png',
    'https://customer-assets.emergentagent.com/job_tss-admission-portal/artifacts/x5te61tk_Screenshot%202026-04-14%20at%206.28.41%E2%80%AFAM.png',
    'https://customer-assets.emergentagent.com/job_tss-admission-portal/artifacts/uk3u8i4y_Screenshot%202026-04-14%20at%205.46.49%E2%80%AFAM.png'
  ];

  const recentArticles = [
    { title: 'Lights Out', slug: 'lights-out' },
    { title: 'Bura Na Mano, It\u2019s Cinema', slug: 'bura-na-mano' },
    { title: 'Thaai Kizhavi: She Gave Them the Gold. Not the Game', slug: 'thaai-kizhavi' },
    { title: 'Measured Violence: How Assi Turns Rape Into a System', slug: 'measured-violence-assi' },
    { title: 'Mayasabha: The 35mm Isolation Ward', slug: 'mayasabha-the-35mm-isolation-ward' },
  ];

  const navItems = [
    { label: 'The Space', href: '#the-space' },
    { label: 'Film Analysis', href: '/writing' },
    { label: 'Episodes', href: '/episodes', accent: true },
    { label: 'The Host', href: '#the-host' },
    { label: 'The Ethos', href: '#the-ethos' },
  ];

  return (
    <section className="scroll-section py-16 sm:py-24" data-testid="agency-details-section">

      {/* Sticky section nav */}
      <nav className="sticky top-0 z-50 py-3 mb-16 sm:mb-24" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center justify-center gap-3 sm:gap-10 px-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={`font-mono text-[10px] sm:text-[12px] tracking-[0.08em] sm:tracking-[0.15em] uppercase hover:opacity-100 transition-opacity duration-300 whitespace-nowrap py-2 ${item.accent ? 'opacity-95' : 'opacity-75'}`}
              style={{ color: item.accent ? '#e8c47a' : '#f0ebe0' }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 sm:px-8">

        {/* === THE HOST (first fold) === */}
        <div id="the-host" className="scroll-mt-12 py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center text-center space-y-6"
          >
            <img
              src="https://media.journoportfolio.com/users/162383/images/profiles/e6c5d0db-3b9e-46a1-9b3c-82425aae1631.jpg"
              alt="Darshim Saxena"
              className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover opacity-90 grayscale brightness-75 contrast-110"
            />
            <Title>Darshim Saxena</Title>
            <div className="font-mono text-[10px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] uppercase opacity-65 px-2 leading-relaxed" style={{ color: '#f0ebe0' }}>
              Writer &middot; Film Analyst &middot; Theatre<br className="sm:hidden" /><span className="hidden sm:inline"> &middot; </span>FTII Screenwriting &middot; Miranda House
            </div>
            <Body>
              Darshim Saxena writes on cinema and culture, looking beyond what a film says to ask why it says it, who it serves, and what it leaves unseen. Her work appears regularly in Millennium Post, Free Press Journal, Deccan Herald and Orissa Post.
            </Body>
          </motion.div>

          {/* Recent articles */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-14"
          >
            <div className="space-y-0">
              {recentArticles.map((a, i) => (
                <Link
                  key={i}
                  to={`/writing/${a.slug}`}
                  className="block py-2.5 sm:py-3 px-3 sm:px-4 text-left hover:bg-white/[0.03] transition-colors duration-300"
                  style={{ borderBottom: '1px solid rgba(240, 235, 224, 0.04)' }}
                >
                  <div className="text-xs sm:text-sm" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0', opacity: 0.8 }}>
                    {a.title}
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link
                to="/writing"
                className="inline-block font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase opacity-80 hover:opacity-100 transition-all duration-300 border border-[#f0ebe0]/20 hover:border-[#f0ebe0]/50 px-6 py-3 rounded-sm"
                style={{ color: '#f0ebe0' }}
              >
                Film Analysis
              </Link>
            </div>
          </motion.div>
        </div>

        <Perforation />

        {/* === THE SPACE === */}
        <div id="the-space" className="scroll-mt-12 min-h-screen flex flex-col justify-center py-16 sm:py-24">
          <TextBlock>
            <Title>The Gap</Title>
            <Body>
              A homemaker, a bureaucrat, a filmmaker and a student don&rsquo;t watch the same film. They bring different lives to it. Two people can sit next to each other, watch the same film, and walk out with entirely different truths.
            </Body>
            <Body>
              The Second Seat begins in that gap.
            </Body>
          </TextBlock>

          <ParallaxImage src={setImages[0]} alt="The Second Seat Studio" />

          <TextBlock>
            <Title>Atmosphere</Title>
            <Body>
              Podcasts are often reduced to gossip. Interviews are often stripped of warmth. This is neither. It is an intentionally alive conversation space.
            </Body>
          </TextBlock>

          <PullQuote>
            &ldquo;You don&rsquo;t need to have made a film to sit here.&rdquo;
          </PullQuote>
        </div>

        <Perforation />

        {/* === THE ETHOS === */}
        <div id="the-ethos" className="scroll-mt-12">
        <TextBlock>
          <Title>An Open Seat</Title>
          <Body>
            A lawyer and a filmmaker do not leave a courtroom drama with the same weight. A teacher and a soldier carry different things out of a war film. Everyone has something real to say about what they see on screen.
          </Body>
        </TextBlock>

        <ParallaxImage src={setImages[1]} alt="The Second Seat Studio" />

        <TextBlock>
          <Title>Craft</Title>
          <Body>
            Why does a certain kind of light make you feel unsafe before anything has happened? Why does a scene feel lonely before a single word is spoken?
          </Body>
          <Body>
            These are questions anyone can answer, because everyone has felt them. We just rarely get to talk about them.
          </Body>
        </TextBlock>

        <Perforation />

        <TextBlock>
          <Title>The Setup</Title>
          <Body>
            Recliners, not desks. Lapel mics, nothing pointed at anyone&rsquo;s face. No fixed structure, no prepared questions. Each conversation runs as long as it needs to.
          </Body>
          <Body>
            It moves between English and Hindi, the way most of us actually talk about the things that matter to us.
          </Body>
        </TextBlock>

        <PullQuote>
          &ldquo;We believe a ticket comes with certain responsibility.&rdquo;
        </PullQuote>

        <TextBlock>
          <Title>A Ticket&rsquo;s Weight</Title>
          <Body>
            Responsibility for what cinema does to a society doesn&rsquo;t rest only with the people who make it. It rests with everyone who watches.
          </Body>
        </TextBlock>

        <ParallaxImage src={setImages[2]} alt="The Second Seat Studio" />

        <TextBlock>
          <Title>Rooted in Hyderabad</Title>
          <Body>
            The show is rooted in Hyderabad. The set is a specific experience, not a portable setup.
          </Body>
        </TextBlock>

        <Perforation />

        {/* AVAILABLE */}
        <TextBlock>
          <Body>
            Available on YouTube, Spotify and Apple Podcasts.
          </Body>
        </TextBlock>

        <div className="my-12 sm:my-16" />
        </div>

      </div>
    </section>
  );
};

export default ScrollContent;

